import React, { useState, useRef, useEffect } from 'react';
import { Image, Download, Share2, Link2, X, Loader2 } from 'lucide-react';
import { shared } from '../three/shared';
import { ZONES } from '../config/zones';
import { useStore } from '../store';

const TAGLINES = {
  healthcare: 'Where care meets cost on the two banks of the river.',
  economy: 'Markets and safety nets, charted from the canopy.',
  'media-books': 'The story of the story — as told by both shores.',
  'culture-fashion': 'Identity and tradition, drifting past each other.',
  'policy-law': 'Order and reform, weighed on the scales of the jungle.',
  religion: 'Faith and doubt along the sacred current.',
  'gender-norms': 'The shifting norms of the ideological wild.',
  technology: 'Progress and caution, coded into the trees.',
  'education-sports': 'How a society teaches — and competes.',
  'environment-travel': 'The land itself, and who gets to roam it.',
};

const PRESETS = [
  { id: 'natural', label: 'Natural' },
  { id: 'golden', label: 'Golden Hour' },
  { id: 'storm', label: 'Storm' },
];

function buildBookmarkUrl() {
  const s = useStore.getState();
  return `${window.location.origin}/?direct=1&zone=${s.currentZone}&time=${s.timeValue.toFixed(2)}&weather=${s.weather}`;
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  let yy = y;
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, yy);
      line = w;
      yy += lineHeight;
    } else line = test;
  }
  if (line) ctx.fillText(line, x, yy);
  return yy;
}

function applyGrade(ctx, W, H, preset) {
  if (preset === 'golden') {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, 'rgba(255,178,64,0.22)');
    g.addColorStop(0.5, 'rgba(255,132,44,0.12)');
    g.addColorStop(1, 'rgba(120,44,12,0.24)');
    ctx.globalCompositeOperation = 'overlay';
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'source-over';
  } else if (preset === 'storm') {
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = 'rgba(120,140,180,0.82)';
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(8,14,28,0.30)';
    ctx.fillRect(0, 0, W, H);
  }
}

async function composePostcard(baseDataUrl, title, subtitle, url, preset) {
  const img = document.createElement('img');
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = baseDataUrl; });

  const W = 1280, H = 800;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  const ar = img.width / img.height, tar = W / H;
  let dw, dh, dx, dy;
  if (ar > tar) { dh = H; dw = H * ar; dx = (W - dw) / 2; dy = 0; }
  else { dw = W; dh = W / ar; dx = 0; dy = (H - dh) / 2; }
  ctx.drawImage(img, dx, dy, dw, dh);

  applyGrade(ctx, W, H, preset);

  const grad = ctx.createLinearGradient(0, H * 0.42, 0, H);
  grad.addColorStop(0, 'rgba(4,7,13,0)');
  grad.addColorStop(0.55, 'rgba(4,7,13,0.72)');
  grad.addColorStop(1, 'rgba(4,7,13,0.97)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, H * 0.4, W, H * 0.6);

  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#D4AF37';
  ctx.font = "700 22px Georgia, 'Times New Roman', serif";
  ctx.fillText('THE JUNGLE OF BIAS', 56, 66);
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.font = "600 13px 'Courier New', monospace";
  ctx.fillText('IDEOLOGICAL ECOLOGY · LIVE SIMULATION', 56, 88);

  ctx.fillStyle = '#ffffff';
  ctx.font = "700 70px Georgia, 'Times New Roman', serif";
  ctx.fillText(title, 56, H - 132);

  ctx.fillStyle = 'rgba(255,255,255,0.82)';
  ctx.font = "300 28px Georgia, 'Times New Roman', serif";
  wrapText(ctx, subtitle, 56, H - 88, W - 112, 36);

  ctx.fillStyle = '#D4AF37';
  ctx.font = "600 18px 'Courier New', monospace";
  ctx.fillText(url.replace(/^https?:\/\//, ''), 56, H - 42);

  ctx.strokeStyle = 'rgba(212,175,55,0.9)';
  ctx.lineWidth = 3;
  ctx.strokeRect(14, 14, W - 28, H - 28);

  return c.toDataURL('image/jpeg', 0.92);
}

export default function Postcard() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [img, setImg] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [preset, setPreset] = useState('natural');
  const baseRef = useRef(null);
  const metaRef = useRef({ title: '', subtitle: '', url: '', fileId: 'view' });

  const currentZone = useStore((s) => s.currentZone);
  const tourCompleteNonce = useStore((s) => s.tourCompleteNonce);
  const lastTour = useRef(0);

  const runCompose = async (p) => {
    if (!baseRef.current) return;
    const { title, subtitle, url } = metaRef.current;
    const composed = await composePostcard(baseRef.current, title, subtitle, url, p);
    setImg(composed);
  };

  const capture = async (opts = {}) => {
    const zone = ZONES[useStore.getState().currentZone] || ZONES[0];
    const title = opts.title || zone.title;
    const subtitle = opts.subtitle || TAGLINES[zone.id] || 'A voyage through the ecology of ideology.';
    const p = opts.preset || 'natural';
    setError(''); setCopied(false); setBusy(true); setOpen(true); setImg(null); setPreset(p);
    try {
      const base = shared.capturePostcard && shared.capturePostcard();
      if (!base) throw new Error('capture-unavailable');
      baseRef.current = base;
      metaRef.current = { title, subtitle, url: buildBookmarkUrl(), fileId: opts.fileId || zone.id };
      const composed = await composePostcard(base, title, subtitle, metaRef.current.url, p);
      setImg(composed);
    } catch (e) {
      setError('Could not capture the view. Try again once the biome has loaded.');
    } finally {
      setBusy(false);
    }
  };

  const pickPreset = async (p) => { setPreset(p); await runCompose(p); };

  // End-of-tour: offer a ready-made highlight postcard.
  useEffect(() => {
    if (tourCompleteNonce > 0 && tourCompleteNonce !== lastTour.current) {
      lastTour.current = tourCompleteNonce;
      const t = setTimeout(() => capture({
        title: 'The Jungle of Bias',
        subtitle: 'Ten biomes of bias, one river between them — a complete traverse.',
        preset: 'golden',
        fileId: 'grand-tour',
      }), 1500);
      return () => clearTimeout(t);
    }
  }, [tourCompleteNonce]);

  const download = () => {
    if (!img) return;
    const a = document.createElement('a');
    a.href = img;
    a.download = `jungle-of-bias-${metaRef.current.fileId}.jpg`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const share = async () => {
    if (!img) return;
    try {
      const blob = await (await fetch(img)).blob();
      const file = new File([blob], `jungle-of-bias-${metaRef.current.fileId}.jpg`, { type: 'image/jpeg' });
      const url = metaRef.current.url;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'The Jungle of Bias', text: `${metaRef.current.title} — explore the ideological Amazon. ${url}` });
        return;
      }
      if (navigator.share) { await navigator.share({ title: 'The Jungle of Bias', text: `${metaRef.current.title} — ${url}`, url }); return; }
      download();
    } catch (e) { /* cancelled/unsupported */ }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(metaRef.current.url || buildBookmarkUrl()); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {}
  };

  return (
    <>
      <button
        data-testid="postcard-btn"
        onClick={() => capture()}
        title="Create a shareable postcard of this view"
        className="pointer-events-auto flex items-center gap-2 px-3.5 py-2.5 glass-gold rounded-full text-gold hover:bg-gold hover:text-black transition-colors duration-300 uppercase tracking-widest text-[10px] font-bold"
      >
        <Image className="w-4 h-4" strokeWidth={1.6} />
        Postcard
      </button>

      {open && (
        <div
          data-testid="postcard-modal"
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm pointer-events-auto animate-hud-in"
          onClick={() => setOpen(false)}
        >
          <div className="relative w-full max-w-2xl glass-gold rounded-2xl border border-gold/30 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-gold">
                <Image className="w-4 h-4" />
                <span className="font-mono text-[11px] uppercase tracking-[0.28em]">Shareable Postcard</span>
              </div>
              <button data-testid="postcard-close-btn" onClick={() => setOpen(false)} className="text-white/50 hover:text-gold transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative rounded-xl overflow-hidden bg-black/60 aspect-[16/10] flex items-center justify-center">
              {busy && (
                <div className="flex flex-col items-center gap-3 text-gold/80">
                  <Loader2 className="w-7 h-7 animate-spin" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Developing…</span>
                </div>
              )}
              {!busy && error && (<p data-testid="postcard-error" className="px-8 text-center text-white/70 text-sm">{error}</p>)}
              {!busy && img && (<img data-testid="postcard-image" src={img} alt="postcard" className="w-full h-full object-contain" />)}
            </div>

            {/* Look presets */}
            <div className="mt-3 flex items-center gap-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mr-1">Look</span>
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`postcard-preset-${p.id}`}
                  onClick={() => pickPreset(p.id)}
                  disabled={busy || !img}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors disabled:opacity-40 ${
                    preset === p.id ? 'glass-gold text-gold border-gold' : 'glass text-white/70 hover:text-gold'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-end gap-2.5">
              <button data-testid="postcard-copy-btn" onClick={copyLink} disabled={busy}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full glass text-white/80 hover:text-gold hover:border-gold transition-colors uppercase tracking-widest text-[10px] font-bold disabled:opacity-40">
                <Link2 className="w-4 h-4" /> {copied ? 'Copied' : 'Copy Link'}
              </button>
              <button data-testid="postcard-download-btn" onClick={download} disabled={busy || !img}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full glass text-white/80 hover:text-gold hover:border-gold transition-colors uppercase tracking-widest text-[10px] font-bold disabled:opacity-40">
                <Download className="w-4 h-4" /> Download
              </button>
              <button data-testid="postcard-share-btn" onClick={share} disabled={busy || !img}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-black hover:bg-[#eccb58] transition-colors uppercase tracking-widest text-[10px] font-bold disabled:opacity-40">
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
